import React, { useState, useEffect } from "react";
import { getOffers, getFallThrough, triageMaintenance, getInvestorOpportunities } from "../../../api";
import { Wrench, Loader2, AlertTriangle, Handshake, TrendingUp, Sparkles } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";

const money = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");
const riskColor = { High: "text-red-600", Medium: "text-amber-600", Low: "text-green-600" };

const AdminOpsIntel = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState("transactions");
  const [offers, setOffers] = useState([]);
  const [risk, setRisk] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desc, setDesc] = useState("The boiler keeps losing pressure and now there is no hot water");
  const [triage, setTriage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [o, r, inv] = await Promise.all([getOffers(), getFallThrough(), getInvestorOpportunities()]);
      setOffers(o.items || []); setRisk(r.items || []); setInvestors(inv.items || []);
      setLoading(false);
    })();
  }, []);

  const runTriage = async (createJob = false) => {
    setBusy(true);
    try { const r = await triageMaintenance({ description: desc, createJob }); setTriage(r); if (createJob) toast({ title: "Maintenance job created" }); }
    catch { toast({ title: "Triage failed", variant: "destructive" }); }
    setBusy(false);
  };

  if (loading) return <div className="py-16 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>;
  const tabBtn = (v, l) => <button onClick={() => setTab(v)} data-testid={`ops-tab-${v}`} className={`px-4 py-2 rounded-lg text-[13px] ${tab === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:bg-[#faf7f2]"}`}>{l}</button>;

  return (
    <div className="space-y-5" data-testid="admin-ops-intel">
      <div>
        <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Wrench size={20} className="text-[#b8935f]" /> Transactions & Management</h2>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Offer likelihood, fall-through risk, AI maintenance triage and investor opportunities. Estimates only.</p>
      </div>
      <div className="flex gap-1.5 bg-white border border-[#f0ece5] rounded-xl p-1.5 w-fit flex-wrap">
        {tabBtn("transactions", "Transactions")}{tabBtn("maintenance", "Maintenance triage")}{tabBtn("investors", "Investor finder")}
      </div>

      {tab === "transactions" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
            <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Handshake size={16} className="text-[#b8935f]" /> Offer probability</h3>
            <div className="space-y-2">{offers.map((o) => (
              <div key={o.id} className="flex items-center gap-3" data-testid={`offer-${o.id}`}>
                <span className="text-[16px] font-bold text-[#b8935f] w-12">{Math.round(o.offerProbability * 100)}%</span>
                <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#2b2b2b] truncate">{o.buyer}</div><div className="text-[12px] text-[#8a8a8a] truncate">{o.property} · {o.stage}</div></div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${o.focus === "High" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{o.focus}</span>
              </div>
            ))}</div>
          </div>
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
            <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-[#b8935f]" /> Fall-through risk</h3>
            <div className="space-y-2">{risk.map((r) => (
              <div key={r.id} className="border border-[#f0ece5] rounded-lg p-2.5" data-testid={`risk-${r.id}`}>
                <div className="flex items-center justify-between"><span className="text-[13px] font-medium text-[#2b2b2b] truncate">{r.property}</span><span className={`text-[13px] font-bold ${riskColor[r.level]}`}>{r.level}</span></div>
                <div className="text-[12px] text-[#8a8a8a]">{r.buyer} · {r.reasons.join(", ")}</div>
              </div>
            ))}</div>
          </div>
        </div>
      )}

      {tab === "maintenance" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4 space-y-3">
            <label className="text-[13px] font-medium text-[#2b2b2b]">Tenant's report</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="triage-input" rows={4} className="w-full text-[13px] border border-[#f0ece5] rounded-lg px-3 py-2" />
            <div className="flex gap-2">
              <button onClick={() => runTriage(false)} disabled={busy} data-testid="triage-run" className="flex-1 bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-lg py-2.5 text-[14px] disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={15} />} Triage</button>
              <button onClick={() => runTriage(true)} disabled={busy || !triage} data-testid="triage-create-job" className="text-[13px] border border-[#f0ece5] rounded-lg px-3 text-[#6a6a6a] hover:bg-[#faf7f2] disabled:opacity-40">Create job</button>
            </div>
          </div>
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="triage-result">
            {!triage ? <div className="text-[13px] text-[#8a8a8a] py-8 text-center">Triage a report to see the assessment.</div> : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className="text-[12px] bg-[#faf7f2] rounded-full px-3 py-1 text-[#4a4a4a]">Category: <b>{triage.category}</b></span>
                  <span className={`text-[12px] rounded-full px-3 py-1 ${["emergency", "high"].includes(triage.urgency) ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>Urgency: {triage.urgency}</span>
                  <span className="text-[12px] bg-[#faf7f2] rounded-full px-3 py-1 text-[#4a4a4a]">Trade: <b>{triage.trade}</b></span>
                </div>
                <p className="text-[13px] text-[#4a4a4a] whitespace-pre-wrap leading-relaxed">{triage.analysis}</p>
                {triage.jobCreated && <div className="text-[12px] text-green-700">✓ Maintenance job created</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "investors" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="investor-grid">
          {investors.map((o) => (
            <div key={o.id} className="bg-white border border-[#f0ece5] rounded-xl overflow-hidden" data-testid={`investor-${o.id}`}>
              {o.image && <img src={o.image} alt={o.title} className="w-full h-28 object-cover" />}
              <div className="p-3 space-y-1.5">
                <div className="text-[13px] font-medium text-[#2b2b2b] truncate">{o.title}</div>
                <div className="flex flex-wrap gap-1">{o.angles.map((a) => <span key={a} className="text-[10px] bg-[#faf7f2] text-[#7a5c2e] rounded-full px-2 py-0.5">{a}</span>)}</div>
                <div className="text-[12px] text-[#8a8a8a]">Price {money(o.price)} · <span className="text-[#b8935f] font-medium">{o.grossYield}% yield</span></div>
                <div className="text-[11px] text-[#8a8a8a]">Est. refurb {money(o.estRefurb)} · GDV {money(o.estGDV)} · margin <b className="text-[#2b2b2b]">{money(o.estGrossMargin)}</b></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOpsIntel;
